import type { DemandRecord } from './mock-data'
import { waPhone } from './debt-reminder'

/**
 * 1.65 — the need record („მოთხოვნის ჩაწერა", 1C's зафиксировать потребность):
 * the till writes down what a buyer asked for and the shop did not have; the
 * owner reads it in the stock manager's „მოთხოვნები" tab and orders from it.
 *
 * The limits are the database's CHECKs (migration 191) — the till refuses what
 * the database would, so a queued need is never dropped for its own content.
 */
export const NEED_LIMITS = { name: 120, qty: 100000, contact: 80, note: 200 } as const

export type NeedInputErrorKey = 'need.err.name' | 'need.err.qty' | 'need.err.contact' | 'need.err.note'

export function needInputError(i: { name: string; qty: number; contact: string; note: string }): NeedInputErrorKey | null {
  const name = i.name.trim()
  if (!name || name.length > NEED_LIMITS.name) return 'need.err.name'
  if (!Number.isFinite(i.qty) || i.qty <= 0 || i.qty > NEED_LIMITS.qty) return 'need.err.qty'
  if (i.contact.trim().length > NEED_LIMITS.contact) return 'need.err.contact'
  if (i.note.trim().length > NEED_LIMITS.note) return 'need.err.note'
  return null
}

export type DemandGroup = {
  key: string
  product_id: string | null
  name: string
  times: number
  qty: number
  /** the newest need's time */
  last: string
  ids: string[]
  /** any of its needs already went into a supplier order */
  ordered: boolean
}

const textKey = (name: string) => name.trim().toLocaleLowerCase('ka')

function group(records: DemandRecord[], keyOf: (r: DemandRecord) => string): DemandGroup[] {
  const by = new Map<string, DemandGroup>()
  for (const r of records) {
    const key = keyOf(r)
    const g = by.get(key)
    if (!g) {
      by.set(key, { key, product_id: r.product_id, name: r.name.trim(), times: 1, qty: r.qty, last: r.created_at, ids: [r.id], ordered: !!r.order_id })
      continue
    }
    g.times += 1
    g.qty += r.qty
    g.ids.push(r.id)
    g.ordered ||= !!r.order_id
    if (r.created_at > g.last) { g.last = r.created_at; g.name = r.name.trim() }
  }
  return [...by.values()].sort((a, b) => b.times - a.times || b.last.localeCompare(a.last))
}

/**
 * The tab's three groups, from the OPEN needs of one branch:
 * - arrived — the product is on the shelf again: a need with a contact stands
 *   alone (someone to call), the rest fold per product;
 * - missing — a product of the catalogue still out of stock, per product;
 * - freeText — the buyer's own words, folded by the trimmed, case-folded text.
 */
export function demandGroups(open: DemandRecord[], stockOf: (productId: string) => number): {
  arrivedWithContact: DemandRecord[]
  arrivedGroups: DemandGroup[]
  missing: DemandGroup[]
  freeText: DemandGroup[]
} {
  const live = open.filter((r) => r.status === 'open')
  const arrived = live.filter((r) => r.product_id && stockOf(r.product_id) > 0)
  const arrivedWithContact = arrived.filter((r) => r.contact?.trim())
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
  return {
    arrivedWithContact,
    arrivedGroups: group(arrived.filter((r) => !r.contact?.trim()), (r) => `p:${r.product_id}`),
    missing: group(live.filter((r) => r.product_id && stockOf(r.product_id) <= 0), (r) => `p:${r.product_id}`),
    freeText: group(live.filter((r) => !r.product_id), (r) => `t:${textKey(r.name)}`),
  }
}

/** A Georgian mobile inside a free-text contact („ნინო 599 12 34 56") → a call and a WhatsApp chat. */
export function contactLinks(contact: string | null): { tel: string | null; wa: string | null } {
  if (!contact) return { tel: null, wa: null }
  const joined = contact.replace(/(\d)[\s\-()]+(?=\d)/g, '$1')
  const m = joined.match(/(?:\+?995)?(5\d{8})(?!\d)/)
  const wa = m ? waPhone(m[1]) : null
  return wa ? { tel: `tel:+${wa}`, wa: `https://wa.me/${wa}` } : { tel: null, wa: null }
}

/** A `demand_records` row as PostgREST sends it (numeric may arrive as a string). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapDemand(r: any): DemandRecord {
  return {
    id: r.id, branch_id: r.branch_id ?? null, shift_id: r.shift_id ?? null, product_id: r.product_id ?? null,
    name: r.name, qty: Number(r.qty), contact: r.contact ?? null, note: r.note ?? null,
    created_by: r.created_by ?? null, created_at: r.created_at, status: r.status === 'closed' ? 'closed' : 'open',
    closed_at: r.closed_at ?? null, order_id: r.order_id ?? null,
  }
}

/**
 * The part of a group still free to order: its open needs not yet on an order, with their quantity (Codex's final
 * review #4 — a group whose needs are partly ordered must not ask the supplier for the ordered part again). Null
 * when nothing of it is free.
 */
export function openPart(g: DemandGroup, records: DemandRecord[]): DemandGroup | null {
  const free = new Map(records.filter((r) => r.status === 'open' && !r.order_id).map((r) => [r.id, r]))
  const ids = g.ids.filter((id) => free.has(id))
  if (ids.length === 0) return null
  return { ...g, ids, times: ids.length, qty: ids.reduce((s, id) => s + free.get(id)!.qty, 0), ordered: false }
}
